import React, { useState } from 'react';
import { User, ChatRoom } from '../../types';
import { useTranslation } from 'react-i18next';
import ChatRoomTitleEditor from './ChatRoomTitleEditor';
import { localizeSystemMessage } from '../../i18n/systemMessages';
import './ChatRoomList.css';

interface ChatRoomListProps {
  rooms: ChatRoom[];
  activeRoom: ChatRoom | null;
  currentUser: User;
  onRoomSelect: (room: ChatRoom) => void;
  onRoomUpdate?: (roomId: number, updates: Partial<ChatRoom>) => void;
  loading: boolean;
}

const ChatRoomList: React.FC<ChatRoomListProps> = ({
  rooms,
  activeRoom,
  currentUser,
  onRoomSelect,
  onRoomUpdate,
  loading
}) => {
  const [editingRoomId, setEditingRoomId] = useState<number | null>(null);
  const { t, i18n } = useTranslation();
  
  // 상대방 정보 가져오기.
  // 주의: 전역 모드(isClientMode)가 아니라 "이 채팅방에서 내가 client인지"로
  // 판단해야 한다. 전역 모드로 분기하면 상단에서 모드를 전환할 때마다
  // 모든 채팅방의 상대방(닉네임/사진)이 실제로는 안 바뀌었는데도 바뀐 것처럼
  // 보이는 버그가 생긴다.
  const getOtherUser = (room: ChatRoom) => {
    const amClientInThisRoom = room.client_id === currentUser?.id;
    return {
      nickname: amClientInThisRoom
        ? room.freelancer_nickname || room.freelancer_name || t('nav.expert')
        : room.client_nickname || room.client_name || t('nav.client'),
      name: amClientInThisRoom
        ? room.freelancer_name
        : room.client_name,
      profileImage: amClientInThisRoom
        ? room.freelancer_profile_image
        : room.client_profile_image
    };
  };

  // 채팅방 제목 생성 (사용자 정의 이름 우선, 없으면 상대방 닉네임)
  const getRoomTitle = (room: ChatRoom) => {
    if (room.room_name) {
      return room.room_name;
    }
    return getOtherUser(room).nickname;
  };

  // 프로필 이미지 또는 초기값 생성
  const getProfileDisplay = (room: ChatRoom) => {
    const otherUser = getOtherUser(room);
    
    if (otherUser.profileImage) {
      return (
        <img 
          src={otherUser.profileImage} 
          alt={otherUser.nickname}
          className="chat-room-avatar-image"
        />
      );
    }
    
    // 프로필 이미지가 없으면 닉네임의 첫 글자 표시
    const initial = otherUser.nickname.charAt(0).toUpperCase();
    return (
      <div className="chat-room-avatar-text">
        {initial}
      </div>
    );
  };

  // 시간 포맷팅 (카카오톡 스타일)
  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor((today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) {
      return t('roomList.justNow');
    } else if (diffMinutes < 60) {
      return t('roomList.minutesShort', { n: diffMinutes });
    } else if (diffHours < 24 && diffDays === 0) {
      return date.toLocaleTimeString('ko-KR', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      });
    } else if (diffDays === 1) {
      return t('chat.yesterday');
    } else if (diffDays < 7) {
      return (i18n.language === 'ko' ? ['일', '월', '화', '수', '목', '금', '토'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])[date.getDay()];
    } else {
      return date.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // 제목 편집 관련 핸들러들
  const handleTitleEdit = (roomId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // 채팅방 선택 이벤트 방지
    setEditingRoomId(roomId);
  };

  const handleTitleUpdate = (roomId: number, newTitle: string) => {
    setEditingRoomId(null);
    
    // 부모 컴포넌트에 업데이트 알림
    if (onRoomUpdate) {
      onRoomUpdate(roomId, { room_name: newTitle });
    }
  };

  const handleTitleCancel = () => {
    setEditingRoomId(null);
  };

  // 마지막 메시지 텍스트 처리
  const getLastMessagePreview = (room: ChatRoom) => {
    if (!room.last_message) {
      return room.project_title ? t('roomList.startProjectConvo') : t('chat.startConvo');
    }
    
    // 긴 메시지는 말줄임표 처리 (시스템 메시지는 언어에 맞춰 변환)
    const msg = localizeSystemMessage(room.last_message);
    return msg.length > 30 ? msg.substring(0, 30) + '...' : msg;
  };

  if (loading) {
    return (
      <div className="chat-room-list-loading">
        <div className="loading-skeleton">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="skeleton-room-item">
              <div className="skeleton-avatar"></div>
              <div className="skeleton-content">
                <div className="skeleton-line skeleton-title"></div>
                <div className="skeleton-line skeleton-message"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="chat-room-list">
      <div className="chat-room-list-header">
        <h2>{t('nav.chat')}</h2>
        <div className="room-count">{rooms.length}</div>
      </div>
      
      <div className="chat-room-list-content">
        {rooms.length === 0 ? (
          <div className="no-chat-rooms">
            <div className="no-chat-icon">💬</div>
            <h3>{t('roomList.noRooms')}</h3>
            <p>{t('roomList.noRoomsDesc')}</p>
          </div>
        ) : (
          <div className="room-list-scroll">
            {rooms.map((room) => {
              const isActive = activeRoom?.id === room.id;
              
              return (
                <div
                  key={room.id}
                  className={`chat-room-item ${isActive ? 'active' : ''}`}
                  onClick={() => onRoomSelect(room)}
                >
                  <div className="chat-room-avatar">
                    {getProfileDisplay(room)}
                    {/* 온라인 상태 표시 (향후 구현) */}
                    {/* <div className="online-indicator"></div> */}
                  </div>
                  
                  <div className="chat-room-content">
                    <div className="chat-room-header">
                      <div className="chat-room-title-container">
                        {editingRoomId === room.id ? (
                          <ChatRoomTitleEditor
                            roomId={room.id}
                            currentTitle={getRoomTitle(room)}
                            onTitleUpdate={handleTitleUpdate}
                            onCancel={handleTitleCancel}
                          />
                        ) : (
                          <div 
                            className="chat-room-title"
                            onDoubleClick={(e) => handleTitleEdit(room.id, e)}
                            title={t('roomList.dblClickEdit')}
                          >
                            {getRoomTitle(room)}
                            <button
                              className="title-edit-button"
                              onClick={(e) => handleTitleEdit(room.id, e)}
                              title={t('roomList.editTitle')}
                            >
                              ✏️
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="chat-room-time">
                        {formatTime(room.last_message_time)}
                      </div>
                    </div>
                    
                    <div className="chat-room-footer">
                      <div className="chat-room-preview">
                        {room.project_title && (
                          <span className="project-tag">
                            📋 {room.project_title}
                          </span>
                        )}
                        <span className="last-message">
                          {getLastMessagePreview(room)}
                        </span>
                      </div>
                      
                      {room.unread_count > 0 && (
                        <div className="unread-badge">
                          {room.unread_count > 999 ? '999+' : room.unread_count}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatRoomList;
